import Fuse from 'fuse.js';
import { i18n } from '../../i18n';

const t = (key: string): string => i18n.global.t(key);

export interface SearchShortcut {
  id: number;
  name: string;
  description: string;
  icon: string;
  url: string;
  searchTerms: string[]; // Index of search terms that should match this shortcut
}

// `name` and `description` are getters so they are translated when read, not at module load.
export const SEARCH_SHORTCUTS: SearchShortcut[] = [
  {
    id: 1,
    get name() { return t('utils.search_shortcuts.create_a_hike'); },
    get description() { return t('utils.search_shortcuts.plot_a_custom_route'); },
    icon: 'pin_drop',
    url: '/hike-planner',
    searchTerms: [
      'create',
      'create a hike',
      'create route',
      'plot',
      'plot route',
      'plan',
      'plan route',
      'plan hike',
      'planner',
      'hike planner',
      'route planner',
      'make route',
      'build route',
      'design route',
      'custom route',
      'new route',
      'new hike'
    ]
  },
  {
    id: 2,
    get name() { return t('utils.search_shortcuts.search_the_hike_map'); },
    get description() { return t('utils.search_shortcuts.find_over_120000_hikes'); },
    icon: 'map',
    url: '/explore',
    searchTerms: [
      'explore',
      'search map',
      'hike map',
      'map',
      'find hikes',
      'find routes',
      'find trails',
      'search hikes',
      'search routes',
      'search trails',
      'browse',
      'browse hikes',
      'discover',
      'discover hikes',
      'look for hikes',
      'find a route',
      'find a hike',
      'find a trail'
    ]
  },
  {
    id: 3,
    get name() { return t('utils.search_shortcuts.view_my_hikes'); },
    get description() { return t('utils.search_shortcuts.all_custom_and_saved_hikes'); },
    icon: 'place',
    url: '/user/hikes',
    searchTerms: [
      'my hikes',
      'my routes',
      'my trails',
      'saved hikes',
      'saved routes',
      'saved trails',
      'my saved hikes',
      'my saved routes',
      'my saved trails',
      'custom hikes',
      'custom routes',
      'my custom hikes',
      'my custom routes',
      'bookmarks',
      'bookmarked hikes',
      'my bookmarks',
      'favourites',
      'favorites',
      'my favourites',
      'my favorites'
    ]
  },
  {
    id: 4,
    get name() { return t('utils.search_shortcuts.view_my_recordings'); },
    get description() { return t('utils.search_shortcuts.all_tracked_hikes'); },
    icon: 'radio_button_checked',
    url: '/user/recordings',
    searchTerms: [
      'my recordings',
      'recordings',
      'tracked hikes',
      'my tracked hikes',
      'gps recordings',
      'my gps',
      'tracked routes',
      'my tracks',
      'tracks',
      'activity',
      'my activity',
      'activities',
      'my activities',
      'history',
      'hike history',
      'past hikes',
      'completed hikes'
    ]
  },
  {
    id: 5,
    get name() { return t('utils.search_shortcuts.view_my_print_maps'); },
    get description() { return t('utils.search_shortcuts.all_printable_maps'); },
    icon: 'print',
    url: '/user/print-maps',
    searchTerms: [
      'print',
      'print maps',
      'printable maps',
      'my print maps',
      'my maps',
      'download maps',
      'pdf maps',
      'offline maps',
      'paper maps',
      'print route',
      'print hike',
      'printable',
      'export map',
      'map pdf'
    ]
  },
  {
    id: 6,
    get name() { return t('utils.search_shortcuts.account_settings'); },
    get description() { return t('utils.search_shortcuts.manage_your_account'); },
    icon: 'settings',
    url: '/user/settings',
    searchTerms: [
      'settings',
      'account',
      'account settings',
      'preferences',
      'my account',
      'profile settings',
      'user settings',
      'configuration',
      'config',
      'manage account',
      'edit account',
      'update profile',
      'change password',
      'privacy',
      'notifications'
    ]
  },
  {
    id: 7,
    get name() { return t('utils.search_shortcuts.help_center'); },
    get description() { return t('utils.search_shortcuts.get_support_and_tips'); },
    icon: 'help',
    url: 'https://support.hiiker.app/',
    searchTerms: [
      'help',
      'help center',
      'support',
      'how to',
      'tutorial',
      'guide',
      'faq',
      'questions',
      'assistance',
      'get help',
      'need help',
      'tips',
      'learn',
      'documentation',
      'docs',
      'contact',
      'contact support'
    ]
  }
];

// Configure Fuse.js instance for searching shortcuts. Built on first search rather than at
// module load, because indexing reads the translated names and descriptions.
let fuse: Fuse<SearchShortcut> | null = null;
const buildFuse = (): Fuse<SearchShortcut> => new Fuse(SEARCH_SHORTCUTS, {
  keys: [
    { name: 'name', weight: 2 },
    { name: 'searchTerms', weight: 3 },
    { name: 'description', weight: 1 }
  ],
  threshold: 0.4,
  includeScore: true,
  ignoreLocation: true,
  useExtendedSearch: false,
  minMatchCharLength: 1
});
const shortcutFuse = (): Fuse<SearchShortcut> => {
  if (!fuse) fuse = buildFuse();
  return fuse;
};

/**
 * Search shortcuts by query string using Fuse.js fuzzy matching
 * @param query - The search query
 * @returns Array of matching shortcuts, sorted by relevance
 */
export function searchShortcuts(query: string): SearchShortcut[] {
  if (!query || query.trim().length === 0) {
    return [];
  }

  const results = shortcutFuse().search(query.trim());
  return results.map(result => result.item);
}
